import { createPathParamCRUD, getAllPathParam, createPathParam } from "@/api/shared/crud";
import { createService } from "@/api/shared/service";
import { getActualTreatmentPlanId, type TPatient } from "../../patient/patient";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";

export * from "./types";
import {
  ETreatmentPlanJobType,
  ETreatmentPlanJobSystemSignal,
  ETreatmentPlanJobManualSignal,
  ETreatmentPlanJobAutomaticSignal,
  ETreatmentPlanJobRecurringSignal,
  ETreatmentPlanJobUiControlType,
  ETreatmentPlanJobDependsOnOperator,
  ETreatmentPlanJobState,
  type TTreatmentPlanJob,
  type TTreatmentPlanJobSignal,
} from "./types";

export { TREATMENT_PLAN_JOB_ORIGIN } from "../jobConstants";

export const jobSelectable = (job: TTreatmentPlanJob) => {
  return {
    title: job.name,
    subtitle: job.signal_type,
    value: job.reference_id,
  };
};

//Creates a default job with initial values
export function createDefaultJob(treatmentPlanId: string): Partial<TTreatmentPlanJob> {
  return {
    reference_id: crypto.randomUUID(),
    treatment_plan: treatmentPlanId,
    name: "",
    signal_type: undefined,
    signal_system_type: undefined,
    signal_manual_type: undefined,
    signal_automatic_type: undefined,
    signal_recurring_type: undefined,
    signal_ui_control_type: undefined,
    depends_on: [],
    depends_on_operator: ETreatmentPlanJobDependsOnOperator.AND,
    exclusion_jobs: [],
    required_read_permissions: [],
    phase: 0,
    for_cooperate_parties_type: [],
  };
}

// Gets the signal subType from a job based on its signal_type
export function getJobSignalSubType(job: Partial<TTreatmentPlanJob>): string | undefined {
  if (!job.signal_type) return undefined;

  switch (job.signal_type) {
    case ETreatmentPlanJobType.SYSTEM:
      return job.signal_system_type;
    case ETreatmentPlanJobType.MANUAL:
      return job.signal_manual_type;
    case ETreatmentPlanJobType.AUTOMATIC:
      return job.signal_automatic_type;
    case ETreatmentPlanJobType.RECURRING:
      return job.signal_recurring_type;
    case ETreatmentPlanJobType.UI_CONTROL:
      return job.signal_ui_control_type;
    default:
      return undefined;
  }
}

/** Match list/embed job payloads (e.g. `jobs_available_for_user`) — same shapes as `treatment_plan/.../jobs_available/`. */
export function jobPayloadMatchesSignal(
  job: Partial<TTreatmentPlanJob>,
  signal: TTreatmentPlanJobSignal
): boolean {
  if (job.signal_type != null) {
    return getJobSignalSubType(job) === signal;
  }
  return (
    job.signal_system_type === signal ||
    job.signal_recurring_type === signal ||
    job.signal_manual_type === signal ||
    job.signal_automatic_type === signal ||
    job.signal_ui_control_type === signal
  );
}

export function findJobBySignal<T extends Partial<TTreatmentPlanJob>>(
  jobs: readonly T[] | undefined | null,
  signal: TTreatmentPlanJobSignal
): T | undefined {
  return jobs?.find((j) => jobPayloadMatchesSignal(j, signal));
}

export function resolveTreatmentPlanJobId(
  job: Partial<TTreatmentPlanJob> | undefined
): string | null {
  if (!job) return null;
  for (const candidate of [job.id, job.reference_id]) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }
  return null;
}

// Sets the signal subType on a job based on its signal_type
export function setJobSignalSubType(job: Partial<TTreatmentPlanJob>, subType: string): void {
  if (!job.signal_type) return;

  switch (job.signal_type) {
    case ETreatmentPlanJobType.SYSTEM:
      job.signal_system_type = subType as ETreatmentPlanJobSystemSignal;
      break;
    case ETreatmentPlanJobType.MANUAL:
      job.signal_manual_type = subType as ETreatmentPlanJobManualSignal;
      break;
    case ETreatmentPlanJobType.AUTOMATIC:
      job.signal_automatic_type = subType as ETreatmentPlanJobAutomaticSignal;
      break;
    case ETreatmentPlanJobType.RECURRING:
      job.signal_recurring_type = subType as ETreatmentPlanJobRecurringSignal;
      break;
    case ETreatmentPlanJobType.UI_CONTROL:
      job.signal_ui_control_type = subType as ETreatmentPlanJobUiControlType;
      break;
  }
}

const SLUG = "treatment_plan";

type RouteParams = { treatment_plan?: string; id?: string };

export interface ITreatmentPlanJobsFilters extends DefaultPaginatedFilters, RouteParams {}

export default createService(SLUG, {
  getAvailableJobs: getAllPathParam<TTreatmentPlanJob, RouteParams>(
    `${SLUG}/patient/{treatment_plan}/jobs_available`
  ),

  getAvailableBackLoopJobs: getAllPathParam<TTreatmentPlanJob, RouteParams>(
    `${SLUG}/patient/{treatment_plan}/backloops_available`
  ),

  ...createPathParamCRUD<TTreatmentPlanJob, RouteParams, ITreatmentPlanJobsFilters>(
    `${SLUG}/treatment-job`
  ),

  changeManualJobState: (
    job: TTreatmentPlanJob,
    targetState: ETreatmentPlanJobState = ETreatmentPlanJobState.COMPLETED
  ) =>
    createPathParam<TTreatmentPlanJob, { id: string }, { state: ETreatmentPlanJobState }>(
      `${SLUG}/patient-job/{id}/change_state`
    )({ id: job.id }, { state: targetState }),

  returnJob: (patient: TPatient, returnJob: TTreatmentPlanJob) =>
    createPathParam<TTreatmentPlanJob, { treatmentPlanId: string }, { id: string }>(
      `${SLUG}/patient/{treatmentPlanId}/return`
    )({ treatmentPlanId: getActualTreatmentPlanId(patient)! }, { id: returnJob.id }),
});
